import React, { FC, useMemo } from "react";
import { ScrollView, View, Layout, Image, useDevice } from "@lookiero/aurora";
import { Text } from "@lookiero/sty-psp-ui";
import { ProductPreview } from "../../../../domain/projections/ProductPreview";
import { useFormatCurrency } from "../../hooks/useFormatCurrency";
import { cdnImageUrl } from "../../shared/util/cdnImageUrl";
import { financial } from "../../shared/util/financial/financial";
import { capitalizeFirstChar } from "../../shared/util/string";
import { TouchableWithoutFeedback } from "../touchableWithoutFeedback/TouchableWithoutFeedback";
import { IMAGE_ASPECT_RATIO, IMAGE_SIZE } from "./ProductCarousel.definition";
import { style as productCarouselStyle } from "./ProductCarousel.styles";

interface ProductCarouselProps {
  readonly title: string;
  readonly products: ProductPreview[];
  readonly onPressProduct: (product: ProductPreview) => void;
}

const ProductCarousel: FC<ProductCarouselProps> = ({ title, products, onPressProduct }) => {
  const { screen } = useDevice();
  const formatCurrency = useFormatCurrency();

  const itemWidth = IMAGE_SIZE.WIDTH - IMAGE_SIZE.GAR_FOR_IMAGE;

  const style = useMemo(() => productCarouselStyle(), []);

  return (
    <View style={style.wrapper}>
      <View style={style.titleWrapper}>
        <Text level={3} variant="heading">
          {title}
        </Text>
      </View>
      <Layout fullWidth>
        <ScrollView contentContainerStyle={products.length === 1 ? style.imagesWrapper : undefined} horizontal>
          <View style={style.imagesContainer}>
            {/* TODO: can we replace this with a flatlist function? */}
            {products.map((product: ProductPreview, index: number) => {
              const lastProduct = index === products.length - 1;
              const gapImage = IMAGE_SIZE.GAP_FOR_IMAGE_SMALL;
              const isProductInSales = product.price.discountedPrice && product.price.discountedPrice.percentage > 0;

              return (
                <TouchableWithoutFeedback
                  key={product.id}
                  testID={`image-${product.id}`}
                  onPress={() => onPressProduct(product)}
                >
                  <View
                    style={[
                      screen.S ? [style.productContainer, lastProduct && style.minMargin] : undefined,
                      {
                        width: itemWidth,
                        marginRight: products.length > 1 && !lastProduct ? gapImage : 0,
                      },
                      style.productWrapper,
                    ]}
                  >
                    <Image
                      alt={product.title}
                      resizeMode="cover"
                      src={cdnImageUrl({ imageUrl: product.imageUrl, width: IMAGE_SIZE.CDN_WIDTH })}
                      style={[
                        {
                          width: itemWidth,
                          height: itemWidth * IMAGE_ASPECT_RATIO,
                        },
                        style.image,
                      ]}
                    />

                    <View style={style.productInfo}>
                      <Text level={2} style={style.productName} variant="detail">
                        {capitalizeFirstChar(product.brand.toLowerCase())}
                      </Text>

                      <Text ellipsizeMode="tail" level={2} variant="detail">
                        {product.title}
                      </Text>

                      {!isProductInSales ? (
                        <Text level={2} variant="detail">
                          {formatCurrency({ amount: financial(product.price.amount) })}
                        </Text>
                      ) : (
                        <View style={style.productDetail}>
                          <View style={style.priceContent}>
                            <Text
                              level={2}
                              style={style.priceWithDiscount}
                              testID="product-discounted-price"
                              variant="detail"
                            >
                              {product.price.discountedPrice &&
                                formatCurrency({ amount: financial(product.price.discountedPrice.amount) })}
                            </Text>

                            <Text level={2} style={style.priceLineThrough} testID="product-price" variant="detail">
                              {formatCurrency({ amount: financial(product.price.amount) })}
                            </Text>

                            <Text style={style.discount}>{`-${product.price.discountedPrice?.percentage}%`}</Text>
                          </View>
                        </View>
                      )}
                    </View>
                  </View>
                </TouchableWithoutFeedback>
              );
            })}
          </View>
        </ScrollView>
      </Layout>
    </View>
  );
};

export { ProductCarousel };
